import React, { useState, useEffect } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './HelpCenter.module.css';

/**
 * HelpCenter — searchable FAQ/help center component.
 *
 * Fetches articles from /api/help/articles with live search.
 *
 * Usage (as a page or section):
 *   {features.helpCenter && <HelpCenter />}
 */
const HelpCenter = ({ className }) => {
  const [query, setQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const fetchArticles = async (q = '') => {
    setIsLoading(true);
    try {
      const params = q ? `?q=${encodeURIComponent(q)}` : '';
      const res = await fetch(`/api/help/articles${params}`);
      const data = await res.json();
      setArticles(data.articles || []);
    } catch (_) {
      setArticles([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchArticles(); }, []);

  const handleSearch = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(window._helpSearchTimer);
    window._helpSearchTimer = setTimeout(() => fetchArticles(val), 300);
  };

  // Group articles by category
  const grouped = articles.reduce((acc, article) => {
    const cat = article.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(article);
    return acc;
  }, {});

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.searchRow}>
        <svg className={css.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          type="text"
          value={query}
          onChange={handleSearch}
          placeholder="Search help articles..."
          className={css.searchInput}
        />
      </div>

      {isLoading ? (
        <p className={css.loading}><FormattedMessage id="HelpCenter.loading" /></p>
      ) : articles.length === 0 ? (
        <p className={css.empty}><FormattedMessage id="HelpCenter.noResults" /></p>
      ) : (
        Object.entries(grouped).map(([category, catArticles]) => (
          <div key={category} className={css.category}>
            <h3 className={css.categoryTitle}>{category}</h3>
            {catArticles.map(article => (
              <div key={article.id} className={css.article}>
                <button
                  type="button"
                  onClick={() => setOpenId(openId === article.id ? null : article.id)}
                  className={css.articleTitle}
                >
                  <span>{article.title}</span>
                  <span className={css.chevron}>{openId === article.id ? '▲' : '▼'}</span>
                </button>
                {openId === article.id && (
                  <div className={css.articleBody}>{article.body}</div>
                )}
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
};

HelpCenter.defaultProps = { className: null };
HelpCenter.propTypes = { className: string };

export default HelpCenter;
